/**
 * Keep a viewport-space rect in sync with a live element.
 *
 * Four signals feed one rAF-coalesced scheduler, because each catches a case
 * the others miss:
 *  - `ResizeObserver` on the element — content grows as data loads.
 *  - `ResizeObserver` on the document element — window and layout reflow.
 *  - `scroll` on every *scrollable ancestor* — the CFO shell scrolls a div, not
 *    the page, so a `window` listener would never fire.
 *  - A short settle ticker after mount — CSS transitions and virtualized lists
 *    move elements without notifying any observer.
 *
 * Everything is torn down through one `AbortController`; a leaked observer in a
 * long tour session is a real memory risk.
 */

import { useEffect, useState } from 'react'
import { rectOf, scrollableAncestors } from './anchor'
import { rectEquals, type Rect } from './position'

/** How long to keep re-measuring every frame after the anchor changes. */
const SETTLE_MS = 600

export function useAnchorRect(element: HTMLElement | null): Rect | null {
  const [rect, setRect] = useState<Rect | null>(() => (element ? rectOf(element) : null))

  useEffect(() => {
    if (!element) {
      setRect(null)
      return
    }
    const controller = new AbortController()
    const { signal } = controller
    let frame = 0
    let current: Rect | null = null

    const measure = () => {
      if (signal.aborted) return
      const next = rectOf(element)
      if (!rectEquals(current, next)) {
        current = next
        setRect(next)
      }
    }

    const schedule = () => {
      if (frame || signal.aborted) return
      frame = requestAnimationFrame(() => {
        frame = 0
        measure()
      })
    }

    measure()

    const resize = new ResizeObserver(schedule)
    resize.observe(element)
    if (element.ownerDocument?.documentElement) resize.observe(element.ownerDocument.documentElement)

    window.addEventListener('resize', schedule, { signal })
    for (const ancestor of scrollableAncestors(element)) {
      ancestor.addEventListener('scroll', schedule, { passive: true, signal })
    }
    // Catch scroll on the page itself too, for surfaces that do scroll the body.
    window.addEventListener('scroll', schedule, { passive: true, capture: true, signal })

    // Settle ticker: measure every frame briefly, so a transition or a
    // virtualized row landing does not leave the card pointing at stale space.
    const settleUntil = Date.now() + SETTLE_MS
    const settle = () => {
      if (signal.aborted) return
      measure()
      if (Date.now() < settleUntil) requestAnimationFrame(settle)
    }
    requestAnimationFrame(settle)

    return () => {
      controller.abort()
      if (frame) cancelAnimationFrame(frame)
      resize.disconnect()
    }
  }, [element])

  return rect
}

/** Viewport size, tracked for placement math. */
export function useViewportSize(): { width: number; height: number } {
  const [size, setSize] = useState(() => ({
    width: typeof window === 'undefined' ? 1280 : window.innerWidth,
    height: typeof window === 'undefined' ? 800 : window.innerHeight,
  }))
  useEffect(() => {
    const controller = new AbortController()
    const onResize = () => setSize({ width: window.innerWidth, height: window.innerHeight })
    window.addEventListener('resize', onResize, { signal: controller.signal })
    onResize()
    return () => controller.abort()
  }, [])
  return size
}
